import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Download, ChevronLeft, ChevronRight, FileText, Info, Play, Pause, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

// Translations for the UI
const translations = {
  en: {
    pageTitle: 'Interview Briefing',
    pageSubtitle: 'Please review the context and questions before starting',
    listen: 'Listen',
    pause: 'Pause',
    loading: 'Loading...',
    download: 'Download',
    downloading: 'Downloading...',
    infoAlert: "This briefing provides context about the interview topics and the questions you'll be asked. Take your time to review it before proceeding to the setup phase.",
    briefingDocument: 'Briefing Document',
    noBriefing: 'No briefing content available yet.',
    noBriefingSubtitle: 'The interviewer is still preparing your briefing.',
    questionsTitle: "Questions You'll Be Asked",
    back: 'Back',
    continueToSetup: 'Continue to Setup',
    downloadError: 'Failed to download briefing. Please try again.',
    audioError: 'Failed to play audio. Your browser may be blocking autoplay.',
    audioFormatError: 'Failed to play audio. The audio format may not be supported.',
    // Question types
    questionTypes: {
      opening: 'opening',
      core: 'core',
      practical: 'practical',
      'forward-looking': 'forward-looking',
      closing: 'closing',
      general: 'general'
    },
    // Priority labels
    priorities: {
      high: 'high',
      medium: 'medium',
      low: 'low'
    }
  },
  es: {
    pageTitle: 'Briefing de la Entrevista',
    pageSubtitle: 'Por favor revise el contexto y las preguntas antes de comenzar',
    listen: 'Escuchar',
    pause: 'Pausar',
    loading: 'Cargando...',
    download: 'Descargar',
    downloading: 'Descargando...',
    infoAlert: 'Este briefing proporciona contexto sobre los temas de la entrevista y las preguntas que se le harán. Tómese su tiempo para revisarlo antes de continuar a la fase de configuración.',
    briefingDocument: 'Documento de Preparación',
    noBriefing: 'El contenido del briefing aún no está disponible.',
    noBriefingSubtitle: 'El entrevistador todavía está preparando su briefing.',
    questionsTitle: 'Preguntas que se le Harán',
    back: 'Atrás',
    continueToSetup: 'Continuar a Configuración',
    downloadError: 'Error al descargar el briefing. Por favor, inténtelo de nuevo.',
    audioError: 'Error al reproducir audio. Su navegador puede estar bloqueando la reproducción automática.',
    audioFormatError: 'Error al reproducir audio. El formato de audio puede no ser compatible.',
    // Question types
    questionTypes: {
      opening: 'apertura',
      core: 'principal',
      practical: 'práctico',
      'forward-looking': 'prospectivo',
      closing: 'cierre',
      general: 'general'
    },
    // Priority labels
    priorities: {
      high: 'alta',
      medium: 'media',
      low: 'baja'
    }
  },
  ca: {
    pageTitle: "Briefing de l'Entrevista",
    pageSubtitle: 'Si us plau, reviseu el context i les preguntes abans de començar',
    listen: 'Escoltar',
    pause: 'Pausar',
    loading: 'Carregant...',
    download: 'Descarregar',
    downloading: 'Descarregant...',
    infoAlert: "Aquest briefing proporciona context sobre els temes de l'entrevista i les preguntes que se li faran. Preneu-vos el temps per revisar-lo abans de continuar a la fase de configuració.",
    briefingDocument: 'Document de Preparació',
    noBriefing: 'El contingut del briefing encara no està disponible.',
    noBriefingSubtitle: 'L\'entrevistador encara està preparant el vostre briefing.',
    questionsTitle: 'Preguntes que se li Faran',
    back: 'Enrere',
    continueToSetup: 'Continuar a Configuració',
    downloadError: 'Error en descarregar el briefing. Si us plau, torneu-ho a provar.',
    audioError: 'Error en reproduir àudio. El vostre navegador pot estar bloquejant la reproducció automàtica.',
    audioFormatError: 'Error en reproduir àudio. El format d\'àudio pot no ser compatible.',
    // Question types
    questionTypes: {
      opening: 'obertura',
      core: 'principal',
      practical: 'pràctic',
      'forward-looking': 'prospectiu',
      closing: 'tancament',
      general: 'general'
    },
    // Priority labels
    priorities: {
      high: 'alta',
      medium: 'mitjana',
      low: 'baixa'
    }
  }
};

type Language = keyof typeof translations;

interface ExpertBriefingScreenProps {
  interview: {
    expertName: string;
    briefing: string;
    language?: string;
    questions: Array<{
      id: string;
      text: string;
      type: string;
      priority?: string;
    }>;
  };
  token: string;
  onNext: () => void;
  onBack: () => void;
}

export function ExpertBriefingScreen({ interview, token, onNext, onBack }: ExpertBriefingScreenProps) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isCleaningUpRef = useRef(false);

  // Get translations for the interview language
  const lang = (interview.language || 'en') as Language;
  const t = translations[lang] || translations.en;

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      isCleaningUpRef.current = true;
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    };
  }, []);

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      const response = await fetch(`/api/expert/interview/download-briefing?token=${encodeURIComponent(token)}`);

      if (!response.ok) {
        throw new Error('Failed to download briefing');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `interview-briefing-${interview.expertName.replace(/\s+/g, '-')}.md`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading briefing:', error);
      alert(t.downloadError);
    } finally {
      setIsDownloading(false);
    }
  };

  const handlePlayPause = async () => {
    try {
      // If already playing, pause
      if (isPlaying && audioRef.current) {
        audioRef.current.pause();
        setIsPlaying(false);
        return;
      }

      // If audio already loaded, just resume
      if (audioRef.current && audioRef.current.src) {
        try {
          await audioRef.current.play();
          setIsPlaying(true);
        } catch (playError) {
          console.error('Error resuming audio:', playError);
          alert(t.audioError);
        }
        return;
      }

      // Load and play audio
      setIsLoadingAudio(true);
      console.log('[TTS] Fetching audio from backend...');

      const response = await fetch('/api/tts/briefing', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ text: interview.briefing })
      });

      console.log('[TTS] Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[TTS] Error response:', errorText);
        throw new Error(`Failed to generate audio: ${response.status}`);
      }

      const audioBlob = await response.blob();
      console.log('[TTS] Audio blob received:', audioBlob.size, 'bytes, type:', audioBlob.type);

      const audioUrl = window.URL.createObjectURL(audioBlob);
      console.log('[TTS] Audio URL created:', audioUrl);

      // Create audio element
      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      // Handle audio events
      audio.onended = () => {
        console.log('[TTS] Audio playback ended');
        setIsPlaying(false);
      };

      audio.onerror = (e) => {
        console.error('[TTS] Audio element error event:', e);
        console.error('[TTS] Audio error details:', audio.error);
        setIsPlaying(false);
        setIsLoadingAudio(false);

        // Don't show error if we're cleaning up (navigating away)
        if (!isCleaningUpRef.current) {
          alert(t.audioFormatError);
        }
      };

      audio.onloadeddata = () => {
        console.log('[TTS] Audio data loaded successfully');
      };

      // Play audio
      console.log('[TTS] Attempting to play audio...');
      try {
        await audio.play();
        console.log('[TTS] Audio playback started successfully');
        setIsPlaying(true);
      } catch (playError: any) {
        console.error('[TTS] Play error:', playError);
        throw new Error(`Failed to play audio: ${playError.message}`);
      }
    } catch (error: any) {
      console.error('[TTS] Error with TTS:', error);
      alert(`${t.audioFormatError}: ${error.message}`);
    } finally {
      setIsLoadingAudio(false);
    }
  };

  // Helper to translate question type
  const translateQuestionType = (type: string): string => {
    return t.questionTypes[type as keyof typeof t.questionTypes] || type;
  };

  // Helper to translate priority
  const translatePriority = (priority: string): string => {
    return t.priorities[priority as keyof typeof t.priorities] || priority;
  };

  return (
    <div className="min-h-screen p-4 py-8">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">{t.pageTitle}</h1>
            <p className="text-muted-foreground">
              {t.pageSubtitle}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handlePlayPause}
              disabled={isLoadingAudio || !interview.briefing}
              className="bg-green-50 hover:bg-green-100 border-green-300"
            >
              {isLoadingAudio ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t.loading}
                </>
              ) : isPlaying ? (
                <>
                  <Pause className="w-4 h-4 mr-2" />
                  {t.pause}
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  {t.listen}
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={handleDownload}
              disabled={isDownloading}
            >
              <Download className="w-4 h-4 mr-2" />
              {isDownloading ? t.downloading : t.download}
            </Button>
          </div>
        </div>

        {/* Info Alert */}
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            {t.infoAlert}
          </AlertDescription>
        </Alert>

        {/* Briefing Content */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {t.briefingDocument}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {interview.briefing ? (
              <div className="space-y-4 text-base leading-relaxed text-gray-800">
                <ReactMarkdown
                  components={{
                    h1: ({ children }) => <h1 className="text-3xl font-bold text-gray-900 mt-8 mb-4">{children}</h1>,
                    h2: ({ children }) => <h2 className="text-2xl font-bold text-gray-900 mt-6 mb-3">{children}</h2>,
                    h3: ({ children }) => <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2">{children}</h3>,
                    p: ({ children }) => <p className="mb-4 text-gray-700 leading-7">{children}</p>,
                    ul: ({ children }) => <ul className="list-disc list-inside space-y-2 mb-4 pl-4">{children}</ul>,
                    ol: ({ children }) => <ol className="list-decimal list-inside space-y-2 mb-4 pl-4">{children}</ol>,
                    li: ({ children }) => <li className="text-gray-700 leading-7">{children}</li>,
                    strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
                    em: ({ children }) => <em className="italic text-gray-700">{children}</em>,
                    a: ({ children, href }) => <a href={href} className="text-blue-600 hover:text-blue-800 underline" target="_blank" rel="noopener noreferrer">{children}</a>,
                    code: ({ children }) => <code className="bg-pink-50 text-pink-700 px-1.5 py-0.5 rounded text-sm font-mono">{children}</code>,
                    pre: ({ children }) => <pre className="bg-gray-100 p-4 rounded-lg overflow-x-auto mb-4">{children}</pre>,
                    blockquote: ({ children }) => <blockquote className="border-l-4 border-gray-300 pl-4 italic text-gray-600 my-4">{children}</blockquote>,
                  }}
                >
                  {interview.briefing}
                </ReactMarkdown>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>{t.noBriefing}</p>
                <p className="text-sm mt-2">{t.noBriefingSubtitle}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Questions Preview */}
        {interview.questions && interview.questions.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>{t.questionsTitle} ({interview.questions.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {interview.questions.map((question, index) => (
                  <div
                    key={question.id}
                    className="flex gap-3 p-4 bg-muted rounded-lg"
                  >
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{question.text}</p>
                      <div className="flex gap-2 mt-2">
                        <span className="text-xs px-2 py-1 bg-background rounded-full">
                          {translateQuestionType(question.type)}
                        </span>
                        {question.priority && (
                          <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full">
                            {translatePriority(question.priority)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Navigation */}
        <div className="flex justify-between pt-4">
          <Button
            variant="outline"
            onClick={onBack}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            {t.back}
          </Button>
          <Button
            onClick={onNext}
            size="lg"
          >
            {t.continueToSetup}
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
